import React, { lazy, Suspense, useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Grid, GridColumn as Column, GridToolbar, GridNoRecords } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { WidgetGridLoader } from "../OI/WidgetGridLoader";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { buttonAction } from "./GridHelpers/listViewHelpers";
import { DropDown, JsxParser } from "../../..";
import { Button, SplitButton } from "@progress/kendo-react-buttons";
import { IntlService } from "@progress/kendo-react-intl";
import { HeaderThElement } from '@progress/kendo-react-data-tools';

import DataLoader from "./DataLoader";
import _moment from "moment";
import WidgetDrillDownHelper from "../OI/WidgetDrillDownHelper";
import Swal from "sweetalert2";
import FilterViewer from "./GridHelpers/FilterViewer";
import CustomCell from "./GridHelpers/CustomCell";
import DetailComponent from "./GridHelpers/DetailComponent";

const ActionButtons = lazy(() => import("./GridHelpers/ActionButtons"));
const ColumnMenu = lazy(() => import("./GridHelpers/ColumnMenu"));

import gridLoader from "./GridHelpers/gridLoader.svg";

import "./OX_GridStyles.scss";
import CustomRow from "./GridHelpers/CustomRow";
import _ from "lodash";
import helpers from "../../helpers";
import moment from "moment";
import HierarchyMyDataCustomCell from "./GridHelpers/HierarchyMyDataCustomCell";

function getOxzionAdapters(core) {
  return {
    profile: core?.make("oxzion/profile").get().key,
    loader: core?.make("oxzion/splash"),
    helper: core?.make("oxzion/link"),
    restClient: core?.make("oxzion/restClient"),
    alertAdapter: core?.make("oxzion/alert"),
  };
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log("Error caught by boundary:", error, errorInfo);
  }

  render() {
    return this.props.children;
  }
}

const OX_Grid = (props) => {
  const {
    columnConfig,
    dataSource,
    dataLoaderComponent,
    core,
    gridDefaultFilters = { skip: 0 },
    canvasElement,
    actions,
    appId,
    onRowClick,
    widgetUUID,
    widgetFilterParams,
    widgetTotalCount,
    operations: gridOperations,
    listOptions,
    actionButtonType,
    pageId,
    parentDiv,
    pageContentId,
    rowTemplate,
    rowConfig,
    isRelationTab,
    elementId,
    relationsParentRoute,
    parent_fileId,
    pageable,
    toolBarTemplate,
    dataItemKey,
    appliedFilters,
    passColumnConfig,
    notif,
    currentRow,
    showColumnHeaders,
    parentRef,
    checkBoxDisplayRule,
  } = props;

  var baseUrl = core?.config("wrapper.url");

  const { profile, loader, helper, restClient, alertAdapter } = useMemo(() => getOxzionAdapters(core), []);
  const rawDataPresent = typeof dataSource === "object" ? true : false;
  const defaultGroups = [...(gridDefaultFilters?.group?.length > 0 ? gridDefaultFilters?.group : [])];
  const OX_GRID_POPUP_COMPONENT = "gridmodalcomponent";
  const OX_GRID_COMPONENT_KEY = "gridmodalkey";
  const OX_GRID_COMPONENT_FILTER_KEY = "gridmodalfilterkey";
  const widgetConfiguration = { ...props.widgetConfiguration, defaultSort: props.widgetConfiguration?.sort || undefined, sort: undefined };

  const [customColumnConfig, setCustomColumnConfig] = useState(columnConfig.map((column, index) => ({ ...column, id: `_${index + 1}_column` }))); //id should be this format for configurable listviews
  const [gridData, setGridData] = useState(rawDataPresent ? { data: dataSource, ...(widgetTotalCount ? { total: widgetTotalCount } : {}) } : { data: [], total: 0 });
  const [selected, setSelected] = useState({});
  const [isFetching, setIsFetching] = useState(false);

  const gridPDFExport = useRef(GridPDFExport);
  const exportToExcelRef = useRef();
  const childRef = useRef();
  const gridRef = useRef();
  const isFetchingRef = useRef(false);

  // this is a custom hook to get the latest value any ref.
  const useLatest = (value) => {
    const ref = useRef(value);
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref;
  };
  const processFilters = (gridFilter) => {
    if (gridFilter) {
      // creating a deep copy of gridDefaultFilter
      let filter = JSON.parse(JSON.stringify(gridFilter));

      // creating filter object for "between, ytd, mtd" operator
      if (typeof filter.value === "object" && (filter.operator === "between" || filter.operator === "ytd" || filter.operator === "mtd")) {
        return {
          logic: "and",
          filters: [
            {
              field: filter.field,
              operator: "gte",
              value: filter.value.start,
            },
            {
              field: filter.field,
              operator: "lte",
              value: filter.value.end,
            },
          ],
        };
      }

      if (filter.filters && Array.isArray(filter.filters)) {
        filter.filters = filter.filters.map(processFilters);
      }
      return filter;
    }
  };

  const convertDateRangeFilters = (gridDefaultFilters) => {
    const filter = gridDefaultFilters.filter;
    const processedFilter = filter?.filters?.length > 0 ? processFilters(filter) : filter;

    return {
      ...gridDefaultFilters,
      ...(processedFilter ? { filter: processedFilter } : {}),
    };
  };

  const defaultFilters = {
    ...(gridDefaultFilters && convertDateRangeFilters(gridDefaultFilters)),
  };

  const mergedFilters = useMemo(() => {
    // Handle edge cases for merging filters
    const hasDefaultFilter = defaultFilters?.filter && typeof defaultFilters.filter === "object";
    const defaultFilterLogic = hasDefaultFilter ? defaultFilters.filter.logic || "and" : "and";
    const defaultFilterFilters = hasDefaultFilter ? (Array.isArray(defaultFilters.filter.filters) ? defaultFilters.filter.filters : []) : [];

    // Handle appliedFilters edge cases - it could be an object with filters array or a single filter object
    let appliedFiltersList = [];
    if (appliedFilters) {
      if (Array.isArray(appliedFilters.filters)) {
        appliedFiltersList = processFilters(appliedFilters);
      } else if (Array.isArray(appliedFilters)) {
        appliedFiltersList = processFilters(appliedFilters);
      } else if (typeof appliedFilters === "object" && appliedFilters.field) {
        // Single filter object
        appliedFiltersList = [processFilters(appliedFilters)];
      }
    }

    // Only merge if there are filters to merge
    if (defaultFilterFilters.length === 0 && appliedFiltersList.length === 0) {
      return defaultFilters;
    }

    return {
      ...defaultFilters,
      filter: {
        logic: defaultFilterLogic,
        filters: [
          ...(hasDefaultFilter ? [defaultFilters.filter] : []),
          ...(appliedFiltersList.filters && Array.isArray(appliedFiltersList.filters) && appliedFiltersList.filters.length > 0
            ? [{ logic: appliedFiltersList.logic, filters: appliedFiltersList.filters }]
            : []),
        ],
      },
    };
  }, [JSON.stringify(defaultFilters), JSON.stringify(appliedFilters)]);

  const [dataState, setDataState] = useState({
    skip: 0,
    group: defaultGroups,
    ...mergedFilters,
  });

  const latestDataState = useLatest(dataState);
  const onSelectionChange = useCallback((event) => {
    //native event is only present when clicking on the checkbox hence preventing row from being selected on row click
    if (!event.nativeEvent) {
      setSelected(event.select);
    }
  }, []);

  const onHeaderSelectionChange = useCallback((event) => {
    setSelected(event.select);
  }, []);

  const refreshHandler = useCallback((hideLoader = false) => {
    childRef?.current?.triggerGetCall?.(hideLoader);
    const event = new CustomEvent("clearEvalCache", {
      bubbles: true,
    });
    document.querySelector(elementId ? elementId : "#navigation_" + appId)?.dispatchEvent(event);
  }, [appId, elementId]);

  const handleIndividualRowClick = (actionItem, dataItem) => {
    actionItem.confirmationMessage
      ? Swal.fire({
          title: actionItem.confirmationMessage,
          html: actionItem.confirmationMessageText,
          width: actionItem.confirmationMessageWidth,
          confirmButtonText: "Agree",
          confirmButtonColor: "#275362",
          showCancelButton: true,
          cancelButtonColor: "#7b7878",
          target: ".PageRender",
        }).then((result) => {
          result.value ? buttonAction(actionItem, dataItem, { core, appId }) : null;
        })
      : actionItem.details
      ? buttonAction(actionItem, dataItem, { core, appId })
      : null;
    actionItem.callback?.(dataItem);
  };

  const onIndividualRowClick = (e) => {
    if (dataLoaderComponent === "WidgetGridLoader") {
      let drillDownTarget = widgetConfiguration["oxzion-meta"]["drillDown"]["target"];
      let drillDownField = widgetConfiguration["oxzion-meta"]["drillDown"]["drillDownField"];
      if (drillDownTarget == "file") {
        let appName = widgetConfiguration["oxzion-meta"]["drillDown"]["nextWidgetId"];
        let eventData = e.dataItem;
        // console.log("Inside the file log Content" + eventData); //Need to open a URL
        launchApplication(eventData, appName, drillDownField);
      } else {
        WidgetDrillDownHelper.drillDownClicked(WidgetDrillDownHelper.findWidgetElement(e.nativeEvent ? e.nativeEvent.target : e.target), e.dataItem);
      }
    } else {
      if (onRowClick) {
        onRowClick?.(e);
      } else {
        const row = e.dataItem;
        const target = e.target.element;
        if (
          target?.hasAttribute(OX_GRID_COMPONENT_FILTER_KEY) &&
          target?.hasAttribute(OX_GRID_COMPONENT_KEY) &&
          target?.hasAttribute(OX_GRID_POPUP_COMPONENT)
        ) {
          let payload = e.dataItem || {};
          const key = e?.target?.getAttribute(OX_Grid.OX_GRID_COMPONENT_KEY);
          const keys = key.split(".");
          while (keys.length > 0) {
            const k = keys.shift();
            payload = payload[k];
            if (typeof payload === "string" && keys.length) {
              try {
                payload = JSON.parse(payload);
              } catch (e) {
                payload = {};
              }
            }
          }
          const event = new CustomEvent("addModalPopup", {
            detail: {
              target: e,
              data: payload,
              filterKey: e?.target?.getAttribute(OX_Grid.OX_GRID_COMPONENT_FILTER_KEY),
            },
            bubbles: true,
          });
          document.querySelector(elementId ? elementId : "#navigation_" + appId).dispatchEvent(event);
          return;
        }
        if (actions) {
          Object.values(actions)
            .filter((value) => value.defaultAction)
            .forEach(
              (value) => eval(value?.rule == null || value.rule.trim().length == 0 || value.rule) && handleIndividualRowClick(value, e.dataItem),
            );
        }
      }
    }
  };

  const launchApplication = (event, selectedApplication, drillDownField) => {
    if (drillDownField) {
      helper.launchApp(
        {
          // pageId: event.target.getAttribute("page-id"),
          pageTitle: event.name,
          // pageIcon: event.target.getAttribute("icon"),
          fileId: event[drillDownField] ? event[drillDownField] : event.uuid,
        },
        selectedApplication,
      );
    }
  };

  const evaluateRule = (rule, dataItem) => {
    try {
      var data = (currentRow && Object.keys(currentRow).length > 0) ? currentRow : (dataItem && Object.keys(dataItem).length > 0) ? dataItem : {};
      var _moment = moment || _moment;
      const string = helpers.ParameterHandler.replaceParams(appId, rule, data).replace(/moment/g, "_moment");
      return eval(string);
    } catch (error) {
      console.error("Error evaluating rule:", error);
      return false;
    }
  };

  //used in eval
  var formatDate = (dateTime, dateTimeFormat) => {
    let userTimezone,
      userDateTimeFomat = null;
    userTimezone = profile.preferences.timezone ? profile.preferences.timezone : _moment.tz.guess();
    userDateTimeFomat = profile.preferences.dateformat ? profile.preferences.dateformat : "YYYY-MM-DD";
    dateTimeFormat ? (userDateTimeFomat = dateTimeFormat) : null;
    const timezone = userTimezone || 'UTC';
    return _moment(dateTime).utc(dateTime, "MM/dd/yyyy HH:mm:ss").clone().tz(timezone).format(userDateTimeFomat);
  };

  //used in eval
  var formatDateWithoutTimezone = (dateTime, dateTimeFormat) => {
    let userDateTimeFomat = dateTimeFormat ? dateTimeFormat : profile.preferences.dateformat ? profile.preferences.dateformat : "YYYY-MM-DD";
    return dateTime ? _moment(dateTime).format(userDateTimeFomat) : "";
  };

  const handlePDFExport = () => {
    setGridData((prev) => {
      loader.show();
      if (prev.data.length > 0) {
        gridPDFExport?.current?.save?.();
        loader.destroy();
      } else {
        loader.destroy();
        Swal.fire({
          icon: "error",
          title: "No records present to export.",
        });
      }
      return prev;
    });
  };

  const fetchDataWithFilter = async (filter) => {
    const url = `/${dataSource}${dataSource.includes("?") ? "&" : "?"}filter=[${JSON.stringify(filter)}]`;
    return await restClient.request("v1", url, {}, "get");
  };

  const exportExcel = (excelConfig, data) => {
    const excelList = columnConfig?.filter((item) => item?.hidden !== true);
    if (data?.length > 0) {
      data = data.map((item) => {
        let tempItem = { ...item };
        excelConfig.columnConfig.map((column) => {
          if (column.cell) {
            var data = tempItem;
            var _moment = _moment; //!can we just use moment here?

            let evalString =
              column.filter === "date" || column.type === "date" //oistudio uses type instead of filter
                ? `formatDate(${column.cell}, ${column.exportExcelFormat ? `"${column.exportExcelFormat}"` : ""})`
                : column.cell
            evalString = evalString.includes("moment") ? evalString.replace(/moment/g, "_moment") : evalString;
            tempItem[column.field] = eval("(" + evalString + ")");
          }
        });
        return tempItem;
      });
      exportToExcelRef?.current?.save(data, excelList);
    }
  };

  const handleDashboardExportToExcel = async (prev) => {
    let dashboardColumnConfig = columnConfig.map(currentCol => currentCol.type == "date" ? {...currentCol, cell: `item.${currentCol.field}`} : currentCol)
    exportExcel({ columnConfig: dashboardColumnConfig }, prev.data || []);
  };

  const handleAppStudioExcelExport = async (prev) => {
    loader.show();
    const exportToExcelConfig = listOptions?.exportToExcel?.config;
    exportToExcelConfig.fetchAll = false;
    try {
      let filterParams = latestDataState.current;
      const total = prev.total || 0;
      const limit = 1000;
      let allData = [];

      if (exportToExcelConfig.fetchAll && total > limit) {
        const totalPages = Math.ceil(total / limit);

        for (let i = 0; i < totalPages; i++) {
          const skip = i * limit;
          const take = i === totalPages - 1 ? total % limit || limit : limit;
          const filter = { ...filterParams, skip, take };
          const response = await fetchDataWithFilter(filter);

          // Merge response data
          if (response?.data) allData = allData.concat(response.data);
        }
      } else {
        const filter = exportToExcelConfig.fetchAll ? { ...filterParams, skip: 0, take: total } : { ...filterParams };
        const response = await fetchDataWithFilter(filter);
        allData = response?.data ?? [];
      }

      exportExcel(exportToExcelConfig, allData);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      loader.destroy();
    }
  };

  const handleMassUpdate = () => {
    const { massUpdate } = listOptions;
    setSelected((prev) => {
      const selectedRecords = Object.keys(prev).filter((eachRecord) => prev[eachRecord]);
      if (selectedRecords.length > 0) {
        if (massUpdate.config.massUpdateType === "form") {
          const event = new CustomEvent("addPage", {
            detail: {
              pageContent: [
                {
                  type: "Form",
                  form_id: massUpdate.config.massUpdateForm,
                  submission_api: "/file/massupdate",
                  data: { fileId: selectedRecords },
                },
              ],
              popupConfig: true,
              parentPage: pageId,
            },
            bubbles: true,
          });
          document.querySelector(elementId ? elementId : "#navigation_" + appId).dispatchEvent(event);
        } else {
          alertAdapter
            .show({
              title: massUpdate.config.massUpdateConfirmationMessage ? massUpdate.config.massUpdateConfirmationMessage : "Are you sure?",
              confirmButtonText: "Yes, confirm",
              cancelButtonText: "No, cancel",
              icon: "question",
            })
            .then((result) => {
              if (result) {
                restClient.request("v1", `/${massUpdate.config.massUpdateApi}`, selectedRecords, "post").then((response) => {
                  if (response.data.popupConfig) {
                    Swal.fire(response.data.popupConfig)
                    return
                  }
                  alertAdapter.show({
                    title: response.status == "success" ? "Saved!" : "Failed!",
                    icon: response.status == "success" ? "success" : "error",
                    message: response.message,
                    confirmButtonText: "Ok",
                  });
                });
              }
            });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Please select records to update.",
        });
      }
      return prev;
    });
  };

  const onExportButtonClick = (e) => {
    if (e.item.name === "Export to PDF") {
      handlePDFExport();
    } else if (e.item.name === "Export to Excel") {
      setGridData((prev) => {
        handleAppStudioExcelExport(prev);
        return prev;
      });
    }
  };

  const renderListOperations = () => {
    //!change checkBoxSelection to massUpdate in all other places
    const { pivotGrid, refreshButton, massUpdate, exportToPdf, exportToExcel } = listOptions || {};

    const OPERATION_CONFIGS = [
      // {
      //   condition: pivotGrid,
      //   config: {
      //     title: this.state.pivotGrid ? "Grid" : "Pivot Grid",
      //     inline: true,
      //     key: "pivotGrid",
      //     icon: this.state.pivotGrid ? "fa-regular fa-table" : "fa-regular fa-table-pivot",
      //     // predefinedCallback: this.togglePivotGrid,
      //   },
      // },
      {
        condition: refreshButton,
        config: {
          title: "Refresh",
          inline: true,
          key: "refreshOXGrid",
          icon: "fa-solid fa-arrows-rotate",
          predefinedCallback: refreshHandler,
        },
      },
      {
        condition: massUpdate?.enabled,
        config: {
          title: "Mass Update",
          inline: true,
          key: "massUpdate",
          icon: "fa-solid fa-list-check",
          predefinedCallback: handleMassUpdate,
        },
      },
      {
        condition: exportToPdf?.enabled && exportToExcel?.enabled,
        config: {
          inline: true,
          element: (
            <SplitButton
              key={"Export"}
              text={"Export"}
              textField="name"
              onItemClick={onExportButtonClick}
              itemRender={({ item }) => (
                <div style={{ padding: "5px" }} key={item.name}>
                  <i style={{ marginRight: "5px" }} className={`${item.icon} manageIcons`}></i>
                  {item.name}
                </div>
              )}
              className={"gridOperationDropdown"}
              popupSettings={{ popupClass: "dropDownButton" }}
              primary={true}
              items={[
                {
                  name: "Export to PDF",
                  icon: "fa fa-file-pdf",
                },
                {
                  name: "Export to Excel",
                  icon: "fa fa-file-excel",
                },
              ]}
            />
          ),
        },
      },
      {
        condition: exportToPdf?.enabled && !exportToExcel?.enabled,
        config: {
          inline: true,
          element: (
            <Button className="btn gridOperationDropdown" onClick={handlePDFExport}>
              <i className="fa fa-file-pdf"></i>
              Export to PDF
            </Button>
          ),
        },
      },
      {
        condition: exportToExcel?.enabled && !exportToPdf?.enabled,
        config: {
          inline: true,
          element: (
            <Button
              className="btn gridOperationDropdown"
              onClick={() =>
                setGridData((prev) => {
                  handleAppStudioExcelExport(prev);
                  return prev;
                })
              }
            >
              <i className="fa fa-file-excel"></i>
              Export to Excel
            </Button>
          ),
        },
      },
    ];
    const operationsList = OPERATION_CONFIGS.filter((operation) => operation.condition).map((operation) => operation.config);
    operationsList.push(...(gridOperations?.actions?.filter((action) => evaluateRule(action.rule)) ?? []));
    if (gridOperations?.type === "dropdown") {
      const itemRender = ({ item }) => (
        <div style={{ padding: "5px" }} key={item.name}>
          <i style={{ marginRight: "5px" }} className={`${item.icon} manageIcons`}></i>
          {item.name}
        </div>
      );

      return operationsList
        ? {
            type: "DropDown",
            className: "gridOperationDropdown",
            buttons: operationsList,
            title: gridOperations?.title || "Options",
            predefinedCallback: (e) => buttonAction(e.item, {}, props),
            itemRender,
          }
        : null;
    }
    return operationsList;
  };

  const renderOperationButtons = () => {
    const operationButtons = renderListOperations();
    if (Object.keys(operationButtons).length > 0) {
      let ev = new CustomEvent("setOperationButtons", {
        detail: {
          operationButtons: operationButtons,
          type: gridOperations,
          pageId: pageId,
          eventOrigin: "OXGrid",
        },
        bubbles: true,
      });
      document.querySelector(elementId ? elementId : "#navigation_" + appId)?.dispatchEvent(ev);
    }
  };

  const addCustomColumns = () => {
    const tempConfig = [];

    // Only add action column for inline modes
    if (actionButtonType?.length > 0 && actions?.filter((each) => !each.defaultAction).length > 0) {
      tempConfig.push({
        columnMenu: false,
        id: `_${columnConfig?.length + 1}_column`,
        persist: true,
        hidden: false,
        show: true,
        reorderable: false,
        groupable: false,
        default: true,
        locked: true,
        width: "200px",
        filterable: false,
        className: "actionButtonColumn",
        cells: {
          data: (e) => {
            return (
              <td {...e.tdProps}>
                <Suspense fallback={<></>}>
                  <ActionButtons
                    currentRow={currentRow}
                    actions={actions}
                    actionButtonType={actionButtonType}
                    core={core}
                    appId={appId}
                    dataItem={e.dataItem}
                    isFetchingRef={isFetchingRef}
                    disableCache={true}
                  />
                </Suspense>
              </td>
            );
          },
        },
        orderIndex: columnConfig?.length,
      });
    }

    tempConfig.length > 0 && setCustomColumnConfig((prev) => [...prev, ...tempConfig]);
  };

  const updateProperty = (property, columnsState) => {
    return customColumnConfig.reduce((acc, currVal) => {
      if (currVal.field) {
        const matchingColumn = columnsState.find((eachColumn) => eachColumn.field && eachColumn.field === currVal.field);
        if (matchingColumn) {
          acc.push({ ...currVal, [property]: matchingColumn[property] });
        } else {
          acc.push(currVal);
        }
      }
      return acc;
    }, []);
  };

  const handleColumnsStateChange = (columnsState, props) => {
    const changedColumns = updateProperty("hidden", columnsState);
    dataLoaderComponent === "DataLoader" && saveColumnConfiguration(changedColumns, gridDefaultFilters);
    changedColumns.push(...customColumnConfig.filter((each) => each.persist));
    setCustomColumnConfig(changedColumns);
    props.onColumnsStateChange(changedColumns);
    setDataState((prev) => {
      const filteredGroups = prev.group
        ? prev.group.filter((group) => {
            const column = changedColumns.find((col) => col.field === group.field);
            return column && !column.hidden;
          })
        : [];

      return {
        ...prev,
        group: filteredGroups,
      };
    });
  };

  const handleColumnReorder = (event) => {
    const changedColumns = updateProperty("orderIndex", event.columns);
    dataLoaderComponent === "DataLoader" &&
      saveColumnConfiguration(changedColumns, { ...gridDefaultFilters, appliedFilters: dataState.appliedFilters, group: dataState.group });
    changedColumns.push(...customColumnConfig.filter((each) => each.persist));
    setCustomColumnConfig(changedColumns);
  };

  const handleGroupChange = (event) => {
    setDataState((prev) => ({ ...prev, group: event.group }));
    dataLoaderComponent === "DataLoader" &&
      saveColumnConfiguration(
        customColumnConfig.filter((eachCol) => !eachCol.persist),
        { ...gridDefaultFilters, appliedFilters: dataState.appliedFilters, group: event.group },
      );
  };

  const saveColumnConfiguration = (
    columnConfig = customColumnConfig,
    gridDefaultFilters = gridDefaultFilters,
    appliedFilters = dataState.appliedFilters,
  ) => {
    pageContentId &&
      restClient.request(
        "v1",
        `userpagecontent/${pageContentId}/save`,
        {
          columnConfig,
          defaultFilters: gridDefaultFilters,
          appliedFilters: appliedFilters,
        },
        "POST",
      );
  };

  const onRelationChange = (e, filerelationApi, isRelationTab_name) => {
    Requests.addRelation(core, appId, {
      child_id: e?.value?.id,
      relation_name: isRelationTab_name,
      parent_id: parent_fileId,
    }).then((data) => {
      data.status == "success" ? refreshHandler() : null;
    });
  };

  //can be more generic and return a element passed in the configuration
  const calculateFooterAggregate = (dataItem, configuration) => {
    let total = 0;
    if (gridData.data) {
      total = gridData.data.reduce((acc, current) => acc + (typeof current[dataItem.field] == "number" ? current[dataItem.field] : 0), 0);
    }
    if (!Number.isNaN(total)) {
      let formattedSum = total;
      if (configuration.format) {
        let kendo_service = new IntlService();
        formattedSum = kendo_service.toString(total, configuration.format);
      }
      return (
        <td colSpan={dataItem.colSpan} style={configuration.style}>
          {configuration.value}
          {formattedSum}
        </td>
      );
    }
    return <td></td>;
  };

  const renderedColumns = useMemo(() => {
    return customColumnConfig.map((column, index) => {
      if (column.cellTemplate) {
        column.cells = {
          data: (e) => (
            <CustomCell
              cellTemplate={typeof column.cellTemplate === "function" ? (dataItem) => column.cellTemplate(dataItem) : column.cellTemplate}
              dataItem={e.dataItem}
              formatDate={formatDate}
              formatDateWithoutTimezone={formatDateWithoutTimezone}
              profile={profile}
              baseUrl={baseUrl}
            />
          ),
        };
      } else if (column.footerAggregate) {
        //this can be made to support both oi studio and app studio list views
        column.footerCell = (e) => calculateFooterAggregate(e, column.footerAggregate);
      }
      return (
        <Column
          key={index}
          // columnMenu={ColumnMenu}
          resizable={true}
          columnMenu={(e) => (
            <Suspense fallback={<></>}>
              <ColumnMenu
                widgetConfiguration={props.widgetConfiguration}
                customColumnConfig={customColumnConfig}
                onColumnsStateChange={handleColumnsStateChange}
                columnMenuProps={e}
              />
            </Suspense>
          )}
          {...column}
          sortable={column.filterable}
        />
      );
    });
  }, [customColumnConfig]);

  const handleDataStateChange = useCallback((e) => {
    setDataState((prev) => ({
      ...(prev ? prev : {}),
      ...e?.dataState,
      filter: prev?.filter,
    }));
  }, []);


  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  const addEventListeners = useCallback(() => {
    const navElement = document.querySelector(elementId ? elementId : "#navigation_" + appId);
    if (navElement) {
      navElement.addEventListener("handleGridRefresh", refreshHandler);
      navElement.addEventListener("contextmenu", handleContextMenu);
    }
    
    // Return cleanup function
    return () => {
      if (navElement) {
        navElement.removeEventListener("handleGridRefresh", refreshHandler);
        navElement.removeEventListener("contextmenu", handleContextMenu);
      }
    };
  }, [elementId, appId, refreshHandler, handleContextMenu]);

  useLayoutEffect(() => {
    addCustomColumns();
  }, []);

  useEffect(() => {
    const cleanup = addEventListeners();
    renderOperationButtons();
    return cleanup;
  }, [addEventListeners]);


  useEffect(() => {
    childRef.current = null;
  }, [dataLoaderComponent]);

  const isFilterable = useMemo(() => {
    return (listOptions?.filterable && columnConfig?.some((col) => col.filterable)) || (widgetConfiguration?.filterable && JSON.parse(widgetConfiguration?.filterable));
  }, [listOptions, columnConfig, widgetConfiguration]);

  const canExportToExcel = useMemo(() => {
    return widgetConfiguration?.["oxzion-meta"]?.exportToExcel && JSON.parse(widgetConfiguration?.["oxzion-meta"]?.exportToExcel);
  }, [widgetConfiguration]);

  const shouldShowGridToolbar = useMemo(() => {
    return isFilterable || canExportToExcel
  }, [isFilterable, canExportToExcel]);

  const gridContent = useMemo(() => (
      <Grid
        {...listOptions}
        {...widgetConfiguration}
        resizable={true}
        style={{ height: "100%" }}
        dataItemKey={dataItemKey || "uuid"}
        reorderable={true}
        select={Object.keys(selected).length > 0 ? selected : undefined}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        selectable={{
          enabled: listOptions?.massUpdate?.enabled || false,
          drag: false,
          cell: false,
          mode: "multiple",
        }}
        sortable={true}
        className={showColumnHeaders ? "oxGrid" : "oxGrid hideColumnHeaders"}
        groupable={dataState?.group?.length > 0}
        onColumnReorder={handleColumnReorder}
        onRowDoubleClick={onIndividualRowClick}
        onDataStateChange={handleDataStateChange}
        onGroupChange={handleGroupChange}
        skip={dataState?.skip}
        total={gridData?.total ?? 0}
        pageable={pageable || widgetConfiguration?.pageable}
        data={gridData}
        group={dataState?.group}
        filterable={isFilterable && listOptions?.hideGlobalFilter} // Only enable column filtering when global filter is hidden
        detail={
          listOptions?.showHierarchy
            ? (e) => (
                <DetailComponent
                  rowTemplate={rowTemplate}
                  dataItem={e.dataItem}
                  core={core}
                  appId={appId}
                  columnConfig={columnConfig}
                  actions={actions}
                  actionButtonType={actionButtonType}
                  parentRef={parentRef || gridRef}
                />
              )
            : undefined
        }
        cells={{
          hierarchy: {
            data: (e) => <HierarchyMyDataCustomCell e={e} rowTemplate={rowTemplate} />,
          },
          headerCell: (props) => (
            <HeaderThElement {...props.thProps} title={props.title} colSpan={1}>
              {props.field === "selected" ? undefined : props.children}
            </HeaderThElement>
          ),
        }}
        rows={{
          data: (e) => (
            <CustomRow
              currentRow={currentRow}
              actions={actions}
              actionButtonType={actionButtonType}
              core={core}
              appId={appId}
              dataLoaderComponent={dataLoaderComponent}
              dataItem={e.dataItem}
              rowId={e.dataItem.uuid || e.dataItem.file_uuid || e.dataItem.id}
              e={e}
              isFetchingRef={isFetchingRef}
              parentRef={parentRef || gridRef}
              checkBoxDisplayRule={checkBoxDisplayRule}
            />
          ),
        }}
      >
        {shouldShowGridToolbar && (
          <GridToolbar size={"small"}>
            <>
              {isFilterable && !listOptions?.hideGlobalFilter && (
                <FilterViewer
                  core={core}
                  appName={props.proc?.metadata?.name}
                  appliedFilters={appliedFilters || dataState.appliedFilters}
                  columnConfig={customColumnConfig.filter((col) => col.className !== "actionButtonColumn")}
                  defaultFilters={gridDefaultFilters}
                  dataState={dataState}
                  saveColumnConfiguration={saveColumnConfiguration}
                  saveAndApplyFilter={(filters) => {
                    filters.finalFilters.filter = processFilters(filters.finalFilters.filter);
                    setDataState((prev) => ({
                      ...prev,
                      skip: 0,
                      filter: filters.finalFilters.filter,
                      appliedFilters: filters.appliedFilters,
                    }));
                  }}
                />
              )}
              {isFetching && <img src={gridLoader} alt="Loading..." />}
            </>
            {canExportToExcel && (
              <Button
                className="btn"
                onClick={() =>
                  setGridData((prev) => {
                    handleDashboardExportToExcel(prev);
                    return prev;
                  })
                }
              >
                <i className="fa fa-file-excel fa-lg"></i>
              </Button>
            )}
          </GridToolbar>
        )}
        <GridNoRecords>There is no data available</GridNoRecords>
        {listOptions?.massUpdate?.enabled && gridData?.data?.length > 0 && (
          <Column field="selected" resizable={false} width="50px" reorderable={false} columnType={"checkbox"} id={`_0_column`} cells={{
            select: {
              data: (e) => {
                const isDisabled = !evaluateRule(listOptions.massUpdate.config.checkBoxDisplayRule, e.dataItem);
                return (
                  <td {...e.tdProps}>
                    {!isDisabled && e.children}
                  </td>
                );
              }
            },
          }} />
        )}
        {renderedColumns}
      </Grid>
    ),
    [gridData, customColumnConfig, isFetching, dataState.group, selected]);

  const gridWrapper = (
    <div style={{ height: "100%" }} ref={gridRef}>
      {gridContent}
    </div>
  );

  return (
    <React.Fragment>
      {dataLoaderComponent === "DataLoader" && !rawDataPresent && (
        <DataLoader
          autorefresh={listOptions?.autoRefresh}
          parentDiv={parentDiv}
          ref={childRef}
          columnConfig={columnConfig}
          onDataRecieved={(data) => setGridData(data)}
          dataState={_.omit(dataState, ["appliedFilters"])}
          passColumnConfig={passColumnConfig}
          core={core}
          url={dataSource}
          gridRef={gridRef}
          appId={appId}
          setIsFetching={setIsFetching}
          notif={notif}
          isFetchingRef={isFetchingRef}
          showHierarchy={listOptions?.showHierarchy}
        />
      )}
      {dataLoaderComponent === "WidgetGridLoader" && (
        <ErrorBoundary>
          <WidgetGridLoader
            columnConfig={columnConfig}
            onDataRecieved={(data) => setGridData(data)}
            dataState={_.omit(dataState, ["appliedFilters"])}
            data={gridData}
            ref={childRef}
            core={core}
            url={dataSource}
            canvasElement={canvasElement}
            uuid={widgetUUID}
            filterParams={widgetFilterParams}
            totalCount={widgetTotalCount}
            gridRef={gridRef}
            setIsFetching={setIsFetching}
            notif={notif}
          />
        </ErrorBoundary>
      )}
      <div id="customActionsToolbar" className="d-flex justify-content-end">
        <div className="w-25">
          {isRelationTab && (
            <DropDown
              args={core}
              mainList={`app/${appId}/${relationsParentRoute}`}
              selectedItem={{
                name: "Select relation",
              }}
              selectedEntityType={"text"}
              preFetch={true}
              onDataChange={(event) => onRelationChange(event, "filerelation", isRelationTab)}
              // disableItem={props.diableField}
              required={false}
            />
          )}
        </div>
      </div>
      {toolBarTemplate?.length > 0 && (
        <JsxParser
          bindings={{
            moment: _moment,
            formatDate: formatDate,
            formatDateWithoutTimezone: formatDateWithoutTimezone,
            profile,
            baseUrl,
          }}
          renderInWrapper={false}
          jsx={toolBarTemplate || "<></>"}
        />
      )}
      <ExcelExport
        fileName={
          listOptions?.exportToExcel?.config?.fileName
            ? listOptions?.exportToExcel?.config?.fileName
            : listOptions?.exportToExcel?.config?.fileNameTemplate
            ? eval(listOptions?.exportToExcel?.config?.fileName?.fileNameTemplate)
            : undefined
        }
        data={gridData.data}
        ref={exportToExcelRef}
      >
        {gridWrapper}
        <GridPDFExport
          ref={(pdfExport) => {
            gridPDFExport.current = pdfExport;
          }}
        >
          {gridContent}
          {/* columns to be included in the pdf export */}
          {renderedColumns.slice(0, actionButtonType?.length > 0 ? renderedColumns.length - 1 : renderedColumns.length)}
        </GridPDFExport>
      </ExcelExport>
    </React.Fragment>
  );
};

export default OX_Grid;
